import { Injectable, inject } from '@angular/core';
import { AuthUser } from '../models/auth/auth.interface';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { SingInResponse, ValidateConfirmationResponse } from '../models/auth/auth.response';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { RecoverPasswordRequest, RegisterRequest, ResetPasswordRequest, SingInRequest } from '../models/auth/auth.request';
import { BaseResponse } from '../models/base.response';
import { jwtDecode } from 'jwt-decode';
import { User } from '../models/user.interface';
import { Router } from '@angular/router';
import { USER_TYPES, SUBSCRIPTION_TYPES } from '../shared/constants';
import { UsersService } from './users.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  signInObservable = new BehaviorSubject<AuthUser | null>(null);
  signOutObservable = new BehaviorSubject<AuthUser | null>(null);
  userUpdatedObservable = new BehaviorSubject<User | null>(null);

  private user: User | null = null;
  private usersService = inject(UsersService);

  constructor(private http: HttpClient, private router: Router) {

  }

  private localStorageTokenKey = 'AUTH_TOKEN';


  isLoggedIn(): boolean {
    return this.getToken() ? true : false;
  }

  getToken(): string | null {
    return localStorage.getItem(this.localStorageTokenKey) ?? null;
  }

  getUser(): User | null {
    return this.user;
  }

  getAuthUser(): AuthUser | null {
    const token = this.getToken();
    if (!token)
      return null;
    return jwtDecode(token);
  }

  /**
   * Actualiza los datos del usuario en el servicio
   * @param userData Los datos del usuario actualizados
   */
  updateUserData(userData: User) {
    this.user = userData;
    this.userUpdatedObservable.next(userData);
  }

  /**
   * Fuerza una recarga de los datos del usuario desde el servidor
   * @returns Promise que se resuelve cuando los datos han sido actualizados
   */
  async refreshUserData(): Promise<boolean> {
    const user = this.getAuthUser();
    if (!user || !user.id) {
      return false;
    }
    
    try {
      return await new Promise<boolean>((resolve) => {
        this.findUserById(user.id).subscribe({
          next: (res) => {
            if (res && res.data) {
              this.user = res.data;
              this.userUpdatedObservable.next(this.user);
              resolve(true);
            } else {
              resolve(false);
            }
          },
          error: () => {
            resolve(false);
          }
        });
      });
    } catch {
      return false;
    }
  }

  async signInApply(token: string): Promise<boolean> {
    localStorage.setItem(this.localStorageTokenKey, token);
    const user = this.getAuthUser();
    if (user) {
      this.signInObservable?.next(user);

      await new Promise<boolean>((resolve, reject) => { 
        this.findUserById(user.id).subscribe({
          next: async res => {
            if (!res.data)
            {
              console.log('ERROR-1');
              this.signOutApply();
              return;
            }
            this.user = res.data;
            this.userUpdatedObservable.next(this.user);
            
            // Validar semana y actualizar flag para usuarios executive
            if (this.user) {
              await this.validateExecutiveWeekAndUpdateFlag(this.user);
            }
            
            resolve(true);
          },
          error: async err => {
            console.log('ERROR', err);
            this.signOutApply();
            reject();
          }
        });
      });
      
    }

    return false;

  }

  async signOutApply() {
    this.signOutObservable?.next(this.getAuthUser());
    localStorage.removeItem(this.localStorageTokenKey);
    localStorage.removeItem("executive-modal-shown-"+this.user?._id);
    this.user=null;
    this.userUpdatedObservable.next(null);
    await this.router.navigate(['/']);
  }

  signin(param: SingInRequest): Observable<SingInResponse> {
    return this.http.post<SingInResponse>(`${environment.apiUrl}/auth/signin`, param);
  }

  register(param: RegisterRequest): Observable<SingInResponse> {
    return this.http.post<SingInResponse>(`${environment.apiUrl}/auth/register`, param);
  }

  notifyVerification(id: string): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/auth/register/notify-verification`, { id: id });
  }

  verifyCode(code: string, id: string): Observable<ValidateConfirmationResponse> {
    return this.http.post<ValidateConfirmationResponse>(`${environment.apiUrl}/auth/register/validate-code`, { id: id, code: code });
  }

  recoverPassword(param: RecoverPasswordRequest): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/auth/recover-password?email=${param.email}`, {});
  }

  resetPassword(param: ResetPasswordRequest): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/auth/reset-password`, param);
  }
  
  /**
   * Obtiene los datos de un usuario por su ID
   * @param id ID del usuario 
   * @param noCache Bandera para evitar el caché
   * @returns Observable con la respuesta
   */
  findUserById(id: string, noCache: boolean = false): Observable<BaseResponse<any>> {
    // Añadir un timestamp para evitar la caché si se solicita
    let url = `${environment.apiUrl}/users/${id}`;
    if (noCache) {
      url += `?t=${new Date().getTime()}`;
    }
    return this.http.get<BaseResponse<any>>(url);
  }

  /**
   * Valida si el usuario ha completado todos los registros iniciales
   * @param user Datos del usuario
   * @returns true si todos los registros están completos, false en caso contrario
   */
  validateUserProfileComplete(user: User): boolean {
    if (!user || !user.profile) {
      return false;
    }

    return user.profile.registerQuestionsComplete === true &&
           user.profile.testNowComplete === true &&
           user.profile.testFutureComplete === true;
  }

  /**
   * Obtiene la ruta de redirección apropiada basada en el estado del perfil del usuario
   * @param user Datos del usuario
   * @returns Ruta a la que debe redirigirse el usuario
   */
  getUserRedirectRoute(user: User): string {
    // Si no hay usuario o tipo, ir a assessment
    if (!user || !user.type) {
      return '/assessment';
    }

    // Para usuarios que no son clientes (coaches, expertos, administradores)
    if (user.type.id !== USER_TYPES.Customer.id) {
      if (user.type.id === USER_TYPES.Coach.id) {
        return '/dashboard/dashboard-coach';
      }

      if (user.type.id === USER_TYPES.Expert.id) {
        return '/dashboard/dashboard-expert';
      }

      return '/dashboard/dashboard-coach';
    }

    // Para clientes, validar el perfil
    if (!user.profile) {
      return '/assessment';
    }

    // Si no ha completado las preguntas de registro
    if (!user.profile.registerQuestionsComplete) {
      return '/auth/complete-data';
    }

    // Si no ha completado el test actual
    if (!user.profile.testNowComplete) {
      return '/assessment';
    }

    // Si no ha completado el test futuro
    if (!user.profile.testFutureComplete) {
      return '/assessment-future';
    }

    // Si no ha completado el onboarding
    if (!user.profile.onboardingComplete) {
      return '/results';
    }

    // Si todo está completo, ir al dashboard según el tipo de suscripción
    if (this.isExecutiveUser(user)) {
      return '/dashboard/dashboard-executive';
    }
    
    return '/dashboard/dashboard-menu';
  }

  /**
   * Valida el perfil del usuario después del login y redirige según corresponda
   * @param user Datos del usuario
   * @returns Promise que se resuelve con la ruta de redirección
   */
  async validateAndRedirectAfterLogin(user: User): Promise<string> {
    const redirectRoute = this.getUserRedirectRoute(user);
    
    // Si el usuario no ha completado todos los registros, mostrar mensaje informativo
    if (!this.validateUserProfileComplete(user)) {
      console.log('Usuario no ha completado todos los registros iniciales');
      console.log('Estado del perfil:', {
        registerQuestionsComplete: user.profile?.registerQuestionsComplete,
        testNowComplete: user.profile?.testNowComplete,
        testFutureComplete: user.profile?.testFutureComplete,
        onboardingComplete: user.profile?.onboardingComplete
      });
    }

    return redirectRoute;
  }

  /**
   * Verifica si el usuario es Executive
   * @param user Datos del usuario
   * @returns true si el usuario es Executive
   */
  private isExecutiveUser(user: User): boolean {
    return !!(
      user &&
      user.type &&
      user.type.id === USER_TYPES.Customer.id && // Customer
      user.type.suscriptionType &&
      user.type.suscriptionType.id === SUBSCRIPTION_TYPES.Executive.id.toString()
    );
  }

  /**
   * Calcula la semana actual del usuario basándose en su fecha de creación
   * @param user Datos del usuario
   * @returns Número de semana actual (1, 2, 3, etc.)
   */
  private calculateCurrentWeek(user: User): number {
    if (!user?.createdAt) {
      console.log('No createdAt found for user, returning week 1 as default');
      return 1;
    }

    const createdAt = new Date(user.createdAt);
    const now = new Date();
    
    // Calcular la diferencia en milisegundos
    const diffInMs = now.getTime() - createdAt.getTime();
    
    // Convertir a días
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    // Calcular la semana (redondeando hacia arriba)
    const week = Math.ceil((diffInDays + 1) / 7);
    
    return week;
  }

  /**
   * Valida la semana del usuario executive y actualiza el flag phoenixSessions7Completed si corresponde
   * @param user Datos del usuario
   */
  private async validateExecutiveWeekAndUpdateFlag(user: User): Promise<void> {
    // Verificar si es usuario executive
    if (!this.isExecutiveUser(user)) {
      return;
    }

    // Calcular la semana actual
    const currentWeek = this.calculateCurrentWeek(user);
    console.log(`Usuario executive detectado. Semana actual: ${currentWeek}`);

    // Validar si está en la semana 10 o 11
    if (currentWeek === 10 || currentWeek === 11) {
      // Verificar si el flag ya está en true para evitar actualizaciones innecesarias
      const stepsAnswers = (user as any).stepsAnswers;
      const phoenixSessions7Completed = stepsAnswers?.step5?.phoenixSessions7Completed;
      
      if (phoenixSessions7Completed === true) {
        console.log('phoenixSessions7Completed ya está en true, no se actualiza');
        return;
      }

      console.log(`Usuario executive en semana ${currentWeek}, actualizando phoenixSessions7Completed`);
      
      try {
        // Actualizar el flag en el backend
        await new Promise<void>((resolve, reject) => {
          this.usersService.updateStep5(
            user._id || user.id,
            'phoenixSessions7Completed',
            new Date().toISOString()
          ).subscribe({
            next: (response) => {
              console.log('Flag phoenixSessions7Completed actualizado exitosamente');
              // Actualizar el usuario local con los datos actualizados
              if (response.data) {
                this.user = response.data;
                this.userUpdatedObservable.next(this.user);
              }
              resolve();
            },
            error: (error) => {
              console.error('Error al actualizar phoenixSessions7Completed:', error);
              // No rechazamos la promesa para que el login continúe incluso si falla la actualización
              resolve();
            }
          });
        });
      } catch (error) {
        console.error('Error inesperado al actualizar phoenixSessions7Completed:', error);
        // No lanzamos el error para que el login continúe
      }
    }
  }

}
